import { Box, Center, Flex, Heading } from '@chakra-ui/layout';
import {
    Spinner,
    useToast,
    useDisclosure,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Input,
    FormControl,
    FormLabel,
    FormHelperText,
} from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import SelectedGameContext from '../context/SelectedGameContext';
import { Timeout } from '../util/types';
import ProfileContext, {
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';
import { useKeycloak } from '@react-keycloak/web';
import config from '../config';
import { ImCross } from 'react-icons/im';
import { FaTrash } from 'react-icons/fa';

export const Owners: React.FC<{}> = (props) => {
    const selectedGame = useContext(SelectedGameContext);
    // TODO overview should be at subpath '/' not '/overview', or redirect '/' to '/overview'
    const profile = useContext(ProfileContext);
    const currLicense = selectedGame?.currLicense;
    const toast = useToast();
    const timeoutRef = useRef<Timeout>();
    const areLicensesLoaded = useRef(false);
    const { isOpen: isFriendOpen, onOpen: onFriendOpen, onClose: onFriendClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const name = currLicense?.configs?.name || 'Unnamed Game';
    const { keycloak } = useKeycloak();
    const [friendEmail, setFriendEmail] = useState('');
    const [ownersObj, setOwnersObj] = useState<any>([]);
    const [isError, setIsError] = useState(false);
    const [helperText, setHelperText] = useState('Enter the registered email you want to add');
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [tryingToAdd, setTryingToAdd] = useState(false);
    const [selectedOwner, setSelectedOwner] = useState('');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setFriendEmail(event.target.value);

    const addOwners = async (emails: string[]) => {
        try {
            const response = await fetch(
                `${config.licenseServiceURL}/license/${currLicense.key}/owners`,
                {
                    headers: {
                        Authorization: `Bearer ${keycloak.token}`,
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                    body: JSON.stringify(emails),
                },
            );
            if (response.ok) {
                const json = await response.json();
                const ownersArray = json.data;
                if (ownersArray.includes(friendEmail)) {
                    setIsError(false);
                    getOwners();
                    setFriendEmail('');
                    setTryingToAdd(false);
                    setEmailIsValid(false);
                    onFriendClose();
                    console.log('Co-worker successfully added to license');
                    return toast({
                        title: 'Success!',
                        description: 'Coworker has successfully been added to the license! Nice.',
                        status: 'success',
                        duration: 9000,
                        isClosable: true,
                    });
                } else {
                    setIsError(true);
                    console.error('Error adding new person: ', response);
                    setHelperText(`Oh no! Error adding email: ${response.type}`);
                    return toast({
                        title: 'Error!',
                        description:
                            'There was a problem adding your co-worker. Are you sure they are registered?',
                        status: 'error',
                        duration: 9000,
                        isClosable: true,
                    });
                }
            } else {
                if (response.status === 403) {
                    console.log('You do not have permission to assign user to this license');
                    return toast({
                        title: 'No Permission!',
                        description: `Aw jeez, it looks like you don't have permission to assign users to the license.`,
                        status: 'error',
                        duration: 9000,
                        isClosable: true,
                    });
                } else {
                    console.log('Unknown error');
                    return toast({
                        title: 'Error!',
                        description:
                            'There was an unknown error. Please refresh the page or try again later',
                        status: 'error',
                        duration: 9000,
                        isClosable: true,
                    });
                }
            }
        } catch (error) {
            throw error;
        }
    };

    const deleteOwner = async () => {
        const resp = await fetch(`${config.licenseServiceURL}/license/${currLicense.key}/owners`, {
            headers: {
                Authorization: `Bearer ${keycloak.token}`,
                'Content-Type': 'application/json',
            },
            method: 'DELETE',
            body: JSON.stringify([selectedOwner]),
        });
        const response = await resp.json();
        if (resp.status === 200) {
            getOwners();
            console.log(`'${selectedOwner}' removed.`);
            return toast({
                title: 'Success',
                description: `'${selectedOwner}' removed from license. Nice.`,
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
        } else {
            console.error('Error deleting owner from license');
            return toast({
                title: 'Error',
                description: `Awkward! Error deleting '${selectedOwner}'. Please try again later...`,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const closeFriendHandler = () => {
        onFriendClose();
        setFriendEmail('');
        setIsError(false);
        setHelperText('Enter the registered email you want to manage');
    };

    const closeDeleteHandler = () => {
        setSelectedOwner('');
        onDeleteClose();
    };

    const friendManager = () => {
        const isValidEmail = (email: string) => {
            return /\S+@\S+\.\S+/.test(email);
        };
        if (!friendEmail) {
            setIsError(true);
            setHelperText('Please enter an email');
        } else if (!isValidEmail(friendEmail)) {
            setIsError(true);
            setHelperText('Please enter a valid email address');
        } else {
            setEmailIsValid(true);
            setHelperText('');
            setIsError(false);
            console.log('Email is valid');
        }
    };

    const addFriendHandler = () => {
        setTryingToAdd(true);
        friendManager();
    };

    const licenseChecker = () => {
        if (!areLicensesLoaded.current) {
            console.log('Experiencing a slow response time...');
            return toast({
                title: 'Still Loading',
                description: 'Hmm, this is taking longer than expected...',
                status: 'warning',
                duration: 9000,
                variant: 'subtle',
                isClosable: true,
            });
        }
    };

    const getOwners = async () => {
        try {
            const response = await fetch(
                `${config.licenseServiceURL}/license/${currLicense.key}/owners`,
                {
                    headers: {
                        Authorization: `Bearer ${keycloak.token}`,
                    },
                },
            );
            if (response.status === 200) {
                const data = await response.json();
                const ownersArray = data.data;
                setOwnersObj(ownersArray);
            } else {
                return toast({
                    title: 'Unable to get information',
                    description: 'Unable to display list of license owners',
                    status: 'error',
                    duration: 9000,
                    variant: 'subtle',
                    isClosable: true,
                });
            }
        } catch (error) {
            console.log(error);
            return;
        }
    };

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {profile.errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {profile.errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    useEffect(() => {
        if (tryingToAdd && emailIsValid) {
            addOwners([friendEmail]);
            setTryingToAdd(false);
        }
    }, [tryingToAdd, emailIsValid]);

    useEffect(() => {
        if (selectedOwner != '') {
            onDeleteOpen();
        }
    }, [selectedOwner]);

    useEffect(() => {
        if (!currLicense?.key) {
            return;
        }
        getOwners();
    }, [currLicense]);

    useEffect(() => {
        areLicensesLoaded.current = profile?.licensesLoaded;
    }, [profile]);

    useEffect(() => {
        timeoutRef.current = setTimeout(licenseChecker, 9000);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const displaySection = () => {
        return (
            <>
                <Flex display={'flex'}>
                    <Heading>{name}</Heading>
                    <Box flex="1"></Box>
                </Flex>
                <Box>
                    <Heading as="h4" size="md">
                        List Of Owners:
                    </Heading>
                    <ul>
                        {ownersObj?.length > 0
                            ? Object.values(ownersObj).map((owner: any, key: number) => {
                                  return (
                                      <li
                                          style={{ margin: '0.5em 1.5em' }}
                                          key={key}
                                          data-cy="license-owner"
                                      >
                                          <p style={{ display: 'flex' }}>
                                              {owner?.email}
                                              <ImCross
                                                  className="delete-icon"
                                                  onClick={() => {
                                                      setSelectedOwner(owner.email);
                                                  }}
                                              />
                                          </p>
                                      </li>
                                  );
                              })
                            : 'Unable to display list of license owners'}
                    </ul>
                    <Button onClick={onFriendOpen} marginTop={'3em'} marginRight={'3em'}>
                        Add Owners
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <Box>
            {currLicense ? (
                displaySection()
            ) : profile.errors.length > 0 ? (
                <>
                    <Heading textAlign={'center'} marginBottom={'1em'}>
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}

            <Modal
                isOpen={isFriendOpen}
                onClose={onFriendClose}
                isCentered
                motionPreset="slideInBottom"
            >
                <ModalOverlay />
                <ModalContent data-cy="manage-owner">
                    <ModalHeader>Add co-worker?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Email Address</FormLabel>
                            <Input
                                type="email"
                                name={'email'}
                                onChange={handleChange}
                                placeholder={'myfriend@friendo.com'}
                                value={friendEmail}
                                isInvalid={isError}
                                errorBorderColor={'crimson'}
                            />
                            <FormHelperText>{helperText}</FormHelperText>
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={closeFriendHandler} colorScheme="gray">
                            Close
                        </Button>
                        <Button mr={3} colorScheme="blue" onClick={addFriendHandler}>
                            Add
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteClose}
                isCentered
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay>
                    <AlertDialogContent data-cy="delete-owner">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete '{selectedOwner}'?
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete '{selectedOwner} from this license'?
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={closeDeleteHandler}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={() => {
                                    deleteOwner();
                                    onDeleteClose();
                                    setSelectedOwner('');
                                }}
                                ml={3}
                            >
                                <FaTrash style={{ marginRight: '1em' }} /> Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};
